import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { DynamicFormService } from 'src/app/services/dynamic-form.service';
import { UserManualService } from 'src/app/services/user-manual.service';
import { UtilService } from 'src/app/services/util.service';
import { SiteService } from 'src/app/services/site.service';
import { Sites } from 'src/app/shared/models/history.model';
import { Subscription, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SkeletonComponent } from '../skeleton/skeleton.component';
import { SwDrawerComponent } from '../sw-drawer/sw-drawer.component';
import { SwSelectComponent } from '../sw-select/sw-select.component';
import { PhpCategorySectionComponent } from '../php-category-section/php-category-section.component';

export interface FormField {
  type: string;
  name: string;
  label: string;
  placeholder?: string;
  value?: any;
  required?: boolean;
  options?: string[];
  min?: number;
  max?: number;
  step?: number;
  pattern?: string;
  minLength?: number;
  maxLength?: number;
  multiple?: boolean;
  accept?: string;
}

export interface DynamicForm {
  _id: string;
  form_data: FormField[] | any;
  form_name?: string;
  name?: string;
  title?: string;
  created_at?: string;
  updated_at?: string;
  deleted?: boolean;
  is_incident_form_type?: boolean;
}

@Component({
  selector: 'app-dynamic-form-ui',
  imports: [FormsModule, ReactiveFormsModule, CommonModule, NgbTooltip, SkeletonComponent, SwDrawerComponent, SwSelectComponent, PhpCategorySectionComponent],
  templateUrl: './dynamic-form-ui.component.html',
  styleUrl: './dynamic-form-ui.component.scss'
})
export class DynamicFormUIComponent implements OnInit, OnDestroy {

  private dynamicFormService = inject(DynamicFormService);
  protected manual = inject(UserManualService);
  private _utils = inject(UtilService);
  private siteService = inject(SiteService);
  private router = inject(Router);

  // The client portal's "Incident categories" section under the forms. Switched off for now; set to
  // true to show it again — nothing else needs to change.
  readonly showIncidentCategories = false;

  formsList: DynamicForm[] = [];
  isLoading: boolean = false;
  getFormSubscription: Subscription;
  deleteFormSubscription: Subscription;
  renameFormSubscription: Subscription;
  getAllSiteSubscription: Subscription;
  loadFormSitesSubscription: Subscription;
  saveSitesSubscription: Subscription;
  otherFormsSitesSubscription: Subscription;
  isDeleting: { [key: string]: boolean } = {};
  renamingFormId: string | null = null;
  renameDraft = '';
  isRenameSaving = false;

  // Manage-sites drawer state
  sitesPanelOpen = false;
  activeSiteForm: DynamicForm | null = null;
  allSite: Sites[] = [];
  selectedSites: string[] = [];
  isLoadingSites = false;
  savingSites = false;
  // Map of siteId -> the OTHER form that already uses it. Selecting one of these warns
  // the user; on save the site is moved here and removed from that form (one form per site).
  sitesTakenByOtherForms: Record<string, { formId: string; formName: string }> = {};
  // formId -> that form's full current site-id list, so we can recompute it after removing
  // a site that the user reassigned to the active form.
  private otherFormSiteLists: Record<string, string[]> = {};
  // Sites we've already warned about this session, so the toast fires once per pick.
  private warnedConflictSites = new Set<string>();

  ngOnInit() {
    this.loadDynamicForms();
    this._utils.sidebarAndHeaderConfig({
      header: true,
      sidebar: true,
    });
  }

  loadDynamicForms() {
    this.isLoading = true;
    this.getFormSubscription = this.dynamicFormService.getDynamicFormList().subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res && res.dynamicForm && res.dynamicForm.length > 0) {
          this.formsList = res.dynamicForm
            .filter((form: DynamicForm) => !form.deleted)
            .sort((a, b) => Number(!!b.is_incident_form_type) - Number(!!a.is_incident_form_type));
        } else {
          this.formsList = [];
        }
      },
      error: (e) => {
        this.isLoading = false;
        this._utils.showErrorInfo('Failed to load forms. Please try again.');
      },
      complete: () => {
        if (this.getFormSubscription) {
          this.getFormSubscription.unsubscribe();
        }
      }
    });
  }

  openFormBuilder(_form?: DynamicForm) {
    this.router.navigate(['/dynamic-form-add']);
  }

  getFormDisplayName(form: DynamicForm, index: number): string {
    return form.form_name || form.name || form.title || `Form ${index + 1}`;
  }

  startRename(form: DynamicForm, index: number) {
    this.renamingFormId = form._id;
    this.renameDraft = this.getFormDisplayName(form, index);
  }

  cancelRename() {
    this.renamingFormId = null;
    this.renameDraft = '';
    this.isRenameSaving = false;
  }

  saveRename(form: DynamicForm, index: number) {
    const name = this.renameDraft.trim();
    if (!name) {
      this._utils.showErrorInfo('Please enter a template name.');
      return;
    }
    const prev = this.getFormDisplayName(form, index);
    if (name === prev) {
      this.cancelRename();
      return;
    }
    this.isRenameSaving = true;
    const payload = { form_name: name, name, form_data: form.form_data };
    this.renameFormSubscription = this.dynamicFormService.updateDynamicForm(form._id, payload).subscribe({
      next: (res) => {
        this.isRenameSaving = false;
        if (res) {
          form.form_name = name;
          this._utils.showErrorSuccess('Template name updated.');
          this.renamingFormId = null;
          this.renameDraft = '';
        } else {
          this._utils.showErrorInfo('Something went wrong!');
        }
      },
      error: () => {
        this.isRenameSaving = false;
        this._utils.showErrorInfo('Failed to update name. Please try again.');
      },
      complete: () => {
        if (this.renameFormSubscription) {
          this.renameFormSubscription.unsubscribe();
        }
      }
    });
  }

  viewForm(form: DynamicForm) {
    this.activeSiteForm = form;
    this.selectedSites = [];
    this.allSite = [];
    this.sitesTakenByOtherForms = {};
    this.otherFormSiteLists = {};
    this.warnedConflictSites.clear();
    this.sitesPanelOpen = true;
    this.loadFormSites(form);
    this.loadOtherFormsSiteAssignments(form);
  }

  // Builds the "site already used by another form" map (and each form's full site list)
  // so we can warn on select and move the site here on save.
  private loadOtherFormsSiteAssignments(activeForm: DynamicForm) {
    const otherForms = this.formsList.filter(f => f?._id && f._id !== activeForm._id);
    if (!otherForms.length) {
      return;
    }
    const requests = otherForms.map(f =>
      this.dynamicFormService.getDynamicFormById(f._id).pipe(
        map((res: any) => ({ form: f, res })),
        catchError(() => of({ form: f, res: null }))
      )
    );
    this.otherFormsSitesSubscription = forkJoin(requests).subscribe((results) => {
      const taken: Record<string, { formId: string; formName: string }> = {};
      const lists: Record<string, string[]> = {};
      for (const { form, res } of results) {
        const data = res?.dynamicForm ?? res ?? {};
        const sites: string[] = Array.isArray(data.sites) ? data.sites
          : Array.isArray(data.site) ? data.site : [];
        lists[form._id] = sites;
        const formName = this.getFormDisplayName(form, this.formsList.indexOf(form));
        for (const siteId of sites) {
          if (siteId && !taken[siteId]) {
            taken[siteId] = { formId: form._id, formName };
          }
        }
      }
      this.sitesTakenByOtherForms = taken;
      this.otherFormSiteLists = lists;
    });
  }

  // Names the conflicting sites (and the form that currently owns them) for warnings.
  private describeSiteConflicts(siteIds: string[]): string {
    return siteIds
      .map(id => {
        const site = this.allSite.find(s => s._id === id);
        const siteName = site?.site_name || 'This site';
        const ownerName = this.sitesTakenByOtherForms[id]?.formName || 'another form';
        return `"${siteName}" is already assigned to "${ownerName}"`;
      })
      .join('. ');
  }

  // On selection change, warn about sites another form already owns — but keep the pick.
  // Saving will move the site to this form and remove it from the other one.
  onSelectedSitesChange(next: string[]) {
    const selected = Array.isArray(next) ? next : [];
    this.selectedSites = selected;
    const selectedSet = new Set(selected);
    // Forget warn-state for de-selected sites so re-adding warns again.
    this.warnedConflictSites.forEach(id => {
      if (!selectedSet.has(id)) {
        this.warnedConflictSites.delete(id);
      }
    });
    // Warn only for conflicting sites just added (not ones already warned about).
    const newConflicts = selected.filter(
      id => this.sitesTakenByOtherForms[id] && !this.warnedConflictSites.has(id)
    );
    if (newConflicts.length > 0) {
      newConflicts.forEach(id => this.warnedConflictSites.add(id));
      this._utils.showErrorInfo('', this.describeSiteConflicts(newConflicts));
    }
  }

  private loadFormSites(form: DynamicForm) {
    if (!form?._id) {
      this.loadAllSites([]);
      return;
    }
    this.isLoadingSites = true;
    this.loadFormSitesSubscription = this.dynamicFormService.getDynamicFormById(form._id).subscribe({
      next: (res: any) => {
        const data = res?.dynamicForm ?? res ?? {};
        const existing: string[] = Array.isArray(data.sites) ? data.sites
          : Array.isArray(data.site) ? data.site : [];
        this.loadAllSites(existing);
      },
      error: () => {
        this.loadAllSites([]);
      }
    });
  }

  private loadAllSites(existing: string[]) {
    this.isLoadingSites = true;
    this.getAllSiteSubscription = this.siteService.getAllSites().subscribe({
      next: (res: any) => {
        this.isLoadingSites = false;
        this.allSite = Array.isArray(res) ? res : [];
        this.selectedSites = existing.filter(id => this.allSite.some(s => s._id === id));
      },
      error: () => {
        this.isLoadingSites = false;
        this.allSite = [];
        this.selectedSites = [];
        this._utils.showErrorInfo('Failed to load sites. Please try again.');
      }
    });
  }

  saveSelectedSites() {
    if (!this.activeSiteForm?._id) {
      this._utils.showErrorInfo('Form ID is missing');
      return;
    }
    const activeFormId = this.activeSiteForm._id;
    const selected = this.selectedSites || [];

    // Sites the user pulled in from another form: each must be removed from its previous
    // owner so it lives only in this form (the latest one to claim it).
    const conflicts = selected.filter(id => this.sitesTakenByOtherForms[id]);
    const removalByForm: Record<string, string[]> = {};
    for (const siteId of conflicts) {
      const owner = this.sitesTakenByOtherForms[siteId];
      if (!owner?.formId) {
        continue;
      }
      if (!removalByForm[owner.formId]) {
        removalByForm[owner.formId] = [...(this.otherFormSiteLists[owner.formId] || [])];
      }
      removalByForm[owner.formId] = removalByForm[owner.formId].filter(s => s !== siteId);
    }

    // Batch: save this form's sites + rewrite each previous owner without the moved site.
    const requests = [
      this.dynamicFormService.updateDynamicFormSites(activeFormId, { site: selected }),
      ...Object.entries(removalByForm).map(([formId, sites]) =>
        this.dynamicFormService.updateDynamicFormSites(formId, { site: sites })
      ),
    ];

    this.savingSites = true;
    this.saveSitesSubscription = forkJoin(requests).subscribe({
      next: () => {
        this.savingSites = false;
        this._utils.showErrorSuccess('Sites updated successfully!');
        this.closeSitesPanel();
      },
      error: () => {
        this.savingSites = false;
        this._utils.showErrorInfo('Failed to update sites. Please try again.');
      }
    });
  }

  closeSitesPanel() {
    this.sitesPanelOpen = false;
    this.activeSiteForm = null;
    this.selectedSites = [];
    this.allSite = [];
    this.sitesTakenByOtherForms = {};
    this.otherFormSiteLists = {};
    this.warnedConflictSites.clear();
  }

  editForm(form: DynamicForm) {
    this.router.navigate(['/dynamic-form-add'], {
      queryParams: { id: form._id, edit: true },
      state: { formData: form }
    });
  }

  deleteForm(form: DynamicForm, index: number) {
    if (!form) {
      return;
    }
    this._utils.confirmationAlert(
      'Delete',
      `Do you want to delete form "${this.getFormDisplayName(form, index)}" ?`,
      (val: boolean) => {
        if (val) {
          this.performDeleteForm(form);
        }
      }
    );
  }

  private performDeleteForm(form: DynamicForm) {
    if (form) {
      this.isDeleting[form._id] = true;
      this.deleteFormSubscription = this.dynamicFormService.deleteDynamicForm(form._id).subscribe({
        next: (res) => {
          this.isDeleting[form._id] = false;
          if (res) {
            this._utils.showErrorSuccess('Form deleted successfully!');
            this.formsList = this.formsList.filter(f => f._id !== form._id);
          } else {
            this._utils.showErrorInfo('Something went wrong!');
          }
        },
        error: (e) => {
          this.isDeleting[form._id] = false;
          this._utils.showErrorInfo('Failed to delete form. Please try again.');
        },
        complete: () => {
          if (this.deleteFormSubscription) {
            this.deleteFormSubscription.unsubscribe();
          }
        }
      });
    }
  }

  isIncidentForm(form: DynamicForm): boolean {
    return form?.is_incident_form_type === true;
  }

  private unwrapFormDataSections(formData: any): any[] {
    if (!formData) return [];
    if (Array.isArray(formData) && formData.length > 0) {
      const first = formData[0];
      if (first?.sections && Array.isArray(first.sections)) {
        return first.sections;
      }
      if (first?.form_data?.sections && Array.isArray(first.form_data.sections)) {
        return first.form_data.sections;
      }
    }
    if (formData.sections && Array.isArray(formData.sections)) {
      return formData.sections;
    }
    return [];
  }

  private getFormFieldsFlat(form: DynamicForm): FormField[] {
    const fd: any = form.form_data;
    if (!fd) return [];
    const sections = this.unwrapFormDataSections(fd);
    if (sections.length > 0) {
      return sections.flatMap((s: any) => (Array.isArray(s.fields) ? s.fields : []));
    }
    if (Array.isArray(fd) && fd.length > 0 && fd[0]?.name) {
      return fd;
    }
    return [];
  }

  getFormFieldCount(form: DynamicForm): number {
    return this.getFormFieldsFlat(form).length;
  }

  getFormPreview(form: DynamicForm): string {
    if (this.isIncidentForm(form)) {
      return 'Used when operators submit alert incident details from the dashboard.';
    }
    const fields = this.getFormFieldsFlat(form);
    if (!fields.length) {
      return 'No fields';
    }
    const fieldTypes = fields.slice(0, 3).map(f => f.label || f.name).join(', ');
    const remaining = fields.length - 3;
    return remaining > 0 ? `${fieldTypes}... (+${remaining} more)` : fieldTypes;
  }

  goBack() {
    window.history.back();
  }

  ngOnDestroy(): void {
    if (this.getFormSubscription) {
      this.getFormSubscription.unsubscribe();
    }
    if (this.deleteFormSubscription) {
      this.deleteFormSubscription.unsubscribe();
    }
    if (this.renameFormSubscription) {
      this.renameFormSubscription.unsubscribe();
    }
    if (this.getAllSiteSubscription) {
      this.getAllSiteSubscription.unsubscribe();
    }
    if (this.loadFormSitesSubscription) {
      this.loadFormSitesSubscription.unsubscribe();
    }
    if (this.saveSitesSubscription) {
      this.saveSitesSubscription.unsubscribe();
    }
    if (this.otherFormsSitesSubscription) {
      this.otherFormsSitesSubscription.unsubscribe();
    }
  }
}
